import React, { createContext, useCallback, useContext, useState } from 'react';

import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { styled } from '@mui/material/styles';

export interface IAlert {
  message: string;
  duration?: number;
  severity?: Severity;
}

interface IAlertCtx {
  alert: null | IAlert;
  showAlert: (alert: IAlert) => void;
  hideAlert: () => void;
}

export enum Severity {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Error = 'error',
}

const AlertCtx = createContext<IAlertCtx>({
  alert: null,
  showAlert: () => {},
  hideAlert: () => {},
});

export function useAlert() {
  return useContext(AlertCtx);
}

const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState<null | IAlert>(null);
  const { message = '', duration = 3000, severity = Severity.Success } = alert || {};

  const showAlert = useCallback((alert) => setAlert(alert), []);
  const hideAlert = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setAlert(null);
  }, []);

  return (
    <AlertCtx.Provider value={{ showAlert, hideAlert, alert }}>
      {children}
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        open={Boolean(message)}
        autoHideDuration={duration}
        onClose={hideAlert}
      >
        <StyledAlert onClose={hideAlert} severity={severity} variant="filled">
          {message}
        </StyledAlert>
      </Snackbar>
    </AlertCtx.Provider>
  );
};

export default AlertProvider;

const StyledAlert = styled(MuiAlert)`
  & .MuiIconButton-root {
    padding: 5px;
  }
`;
